import * as React from 'react';
import PropTypes from 'prop-types';

import {
    FormControlLabel, RadioGroup, Radio, Button, Input, Typography, makeStyles,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
    choiceResponse: {
        border: `1px solid ${theme.palette.primary.main}`,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.common.white,
    },
    formControlRoot: {
    },
    formControlLabel: {
        margin: theme.spacing(1, 0),
        flex: '1 1 auto',

        '$correct &': {
            color: theme.colors.green,
        },
        '$inCorrect &': {
            color: theme.colors.red,
        },
    },
    choiceButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    choiceButton: {
        margin: theme.spacing(1, 2),
    },
    choiceFreetextInput: {
        border: `1px solid ${theme.palette.grey[200]}`,
        padding: theme.spacing(0, 1),
    },
    radio: {
        color: theme.colors.primary,
        '$correct &': {
            color: theme.colors.green,
        },
        '$inCorrect &': {
            color: theme.colors.red,
        },
    },
    correct: {
        color: `${theme.colors.green}`,
    },
    inCorrect: {
        color: `${theme.colors.red}`,
    },
    freetextLabel: {
        display: 'flex',
        alignItems: 'center',
    },
    freetextLabelText: {
        marginRight: theme.spacing(1),
        minWidth: 80,
    },
}));

const SingleChoice = ({
    choiceConfig, selectedChoice, handleResponseUpdate, showScores, translations, question, disabled,
}) => {
    const classes = useStyles();

    const isDisabled = disabled || showScores;

    const handleSingleSelectChange = answers => (event) => {
        const id = Number(event.target.value);
        const answer = answers.find(d => d.id === id) || { id, value: '' };

        handleResponseUpdate([answer]);
    };

    const handleSelectFreetextChange = freetextId => (event) => {
        handleResponseUpdate([{ id: Number(freetextId), score: 0, value: event.target.value }]);
    };

    const handleClickResponse = value => () => {
        handleResponseUpdate([value]);
    };

    const getScoreClass = (item) => {
        if (!showScores) {
            return null;
        }

        if (item.score > 0) {
            return classes.correct;
        }
        if (selectedChoice?.id === item.id) {
            return classes.inCorrect;
        }
        return null;
    };

    return (
        <>
            {question.type === 'ActionChoice' || choiceConfig?.showAsButtons ? (
                <div className={classes.choiceButtonContainer}>
                    {question.answers?.map(answer => (
                        <Button
                            disabled={isDisabled}
                            key={answer.id}
                            color={selectedChoice?.id === answer.id ? 'primary' : 'default'}
                            variant="contained"
                            size="small"
                            className={classes.choiceButton}
                            onClick={handleClickResponse(answer)}
                        >
                            {answer.value}
                        </Button>
                    ))}
                </div>
            ) : (
                <div className={classes.choiceResponse}>
                    <RadioGroup
                        className={classes.group}
                        value={selectedChoice?.id || ''}
                        onChange={handleSingleSelectChange(question.answers)}
                    >
                        {question.answers?.map(item => (
                            <FormControlLabel
                                disabled={isDisabled}
                                className={getScoreClass(item)}
                                key={item.id}
                                value={item.id}
                                control={<Radio className={classes.radio} />}
                                classes={{ label: classes.formControlLabel, root: classes.formControlRoot }}
                                label={item.value}
                            />
                        ))}

                        {choiceConfig?.freetextChoiceId && (
                            <FormControlLabel
                                disabled={isDisabled}
                                value={choiceConfig.freetextChoiceId}
                                control={<Radio className={classes.radio} />}
                                classes={{ label: classes.formControlLabel, root: classes.formControlRoot }}
                                label={(
                                    <div className={classes.freetextLabel}>
                                        <Typography className={classes.freetextLabelText}>
                                            {choiceConfig.freetextChoiceLabel || translations?.freetextChoiceLabel}
                                        </Typography>
                                        <Input
                                            placeholder={translations?.freetextChoice}
                                            fullWidth
                                            disabled={isDisabled}
                                            value={selectedChoice?.id === choiceConfig.freetextChoiceId ? selectedChoice.value : ''}
                                            onChange={handleSelectFreetextChange(choiceConfig.freetextChoiceId)}
                                            className={classes.choiceFreetextInput}
                                            disableUnderline
                                        />
                                    </div>
                                )}
                            />
                        )}
                    </RadioGroup>
                </div>
            )}
        </>
    );
};

SingleChoice.propTypes = {
    translations: PropTypes.object,
    handleResponseUpdate: PropTypes.func,
    question: PropTypes.object.isRequired,
    choiceConfig: PropTypes.object,
    selectedChoice: PropTypes.object,
    disabled: PropTypes.bool,
    showScores: PropTypes.bool,
};

SingleChoice.defaultProps = {
    choiceConfig: null,
    selectedChoice: null,
    disabled: false,
    translations: null,
    handleResponseUpdate: null,
    showScores: false,
};

export default SingleChoice;
